"use client";

import type { IntegrationAccessToken, LinkPayload } from "@meshconnect/web-link-sdk";

import { MESH_ACCESS_TOKENS } from "@/constants/local-storage-keys";
import { INTEGRATION_ID_TO_BROKER } from "@/constants/mesh";

import { Logger } from "./logger";

const scopedKey = (accountAddress: string) => `${MESH_ACCESS_TOKENS}:${accountAddress}`;

// One-time migration: purge the legacy unscoped key on module load so that
// affected users reconnect Mesh once. Runs at import time (per-page-load),
// not on every `get()` call.
if (typeof window !== "undefined") {
  try {
    localStorage.removeItem(MESH_ACCESS_TOKENS);
  } catch (error) {
    Logger.error(error, { message: "Failed to purge legacy Mesh token key" });
  }
}

const convertToIntegrationAccessTokens = (payload: LinkPayload): IntegrationAccessToken[] => {
  const { accessToken } = payload;
  if (!accessToken) return [];

  return accessToken.accountTokens.map((accountToken) => ({
    accountId: accountToken.account.accountId,
    accountName: accountToken.account.accountName,
    accessToken: accountToken.accessToken,
    brokerType: accessToken.brokerType,
    brokerName: accessToken.brokerName,
  }));
};

export const meshTokenStore = (accountAddress: string) => {
  const key = scopedKey(accountAddress);

  const get = (): IntegrationAccessToken[] => {
    if (typeof window === "undefined") return [];
    try {
      const stored = localStorage.getItem(key);
      return stored ? (JSON.parse(stored) as IntegrationAccessToken[]) : [];
    } catch (error) {
      Logger.error(error, { message: "Failed to retrieve Mesh Connect tokens" });
      return [];
    }
  };

  return {
    store: (payload: LinkPayload): void => {
      if (typeof window === "undefined") return;
      try {
        const tokens = convertToIntegrationAccessTokens(payload);
        if (tokens.length === 0) return;
        localStorage.setItem(key, JSON.stringify(tokens));
      } catch (error) {
        Logger.error(error, { message: "Failed to store Mesh Connect tokens" });
      }
    },
    get,
    getForIntegration: (integrationId: string | undefined): IntegrationAccessToken[] => {
      const all = get();
      if (!integrationId) return all;
      const brokerType = INTEGRATION_ID_TO_BROKER[integrationId]?.type;
      if (!brokerType) return [];
      return all.filter((t) => t.brokerType === brokerType);
    },
    clear: (): void => {
      if (typeof window === "undefined") return;
      try {
        localStorage.removeItem(key);
      } catch (error) {
        Logger.error(error, { message: "Failed to clear Mesh Connect tokens" });
      }
    },
    clearForIntegration: (integrationId: string | undefined): void => {
      if (typeof window === "undefined") return;
      if (!integrationId) return;
      const brokerType = INTEGRATION_ID_TO_BROKER[integrationId]?.type;
      if (!brokerType) return;
      try {
        const remaining = get().filter((t) => t.brokerType !== brokerType);
        if (remaining.length === 0) {
          localStorage.removeItem(key);
        } else {
          localStorage.setItem(key, JSON.stringify(remaining));
        }
      } catch (error) {
        Logger.error(error, { message: "Failed to clear Mesh Connect tokens for integration" });
      }
    },
  };
};
